import { useState } from 'react';
import { Btn } from '../components/Btn.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { NotCompliantModal } from './NotCompliantModal.tsx';
import type { CompanyDocument, Question, SubmissionStatus } from '../lib/types.ts';
import type { ReviewPayload } from './useReviewMutation.ts';

// The Compliant / Not Compliant / N/A cluster shared by the driver and asset
// detail pages. Toggling an active status sends 'reset'. "Not Compliant" opens
// the shared modal to collect the required comment/reference, then sends
// 'reject'. The parent owns the mutation and passes pending/pendingDecision so
// the spinner lands on the right button.
export function QuestionReviewButtons({
  question,
  status,
  documents,
  pending,
  pendingDecision,
  error,
  onDecide,
}: {
  question: Question;
  status: SubmissionStatus | undefined;
  documents: CompanyDocument[];
  pending: boolean;
  pendingDecision: ReviewPayload['decision'] | undefined;
  error: Error | null;
  onDecide: (payload: ReviewPayload) => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isNa = status === 'not_applicable';
  const loadingFor = (target: 'approve' | 'reject' | 'na', active: boolean) =>
    pending && (pendingDecision === target || (pendingDecision === 'reset' && active));

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <Btn
        size="sm"
        variant={isApproved ? 'success' : 'secondary'}
        aria-pressed={isApproved}
        loading={loadingFor('approve', isApproved)}
        onClick={() => onDecide({ decision: isApproved ? 'reset' : 'approve' })}
      >
        <NavIcon name="check-circle" size={16} />
        Compliant
      </Btn>
      <Btn
        size="sm"
        variant={isRejected ? 'danger' : 'secondary'}
        aria-pressed={isRejected}
        loading={loadingFor('reject', isRejected)}
        onClick={() => (isRejected ? onDecide({ decision: 'reset' }) : setRejectOpen(true))}
      >
        <NavIcon name="x-circle" size={16} />
        Not Compliant
      </Btn>
      <Btn
        size="sm"
        variant={isNa ? 'warning' : 'secondary'}
        aria-pressed={isNa}
        loading={loadingFor('na', isNa)}
        onClick={() => onDecide({ decision: isNa ? 'reset' : 'na' })}
      >
        N/A
      </Btn>

      {rejectOpen && (
        <NotCompliantModal
          question={question}
          documents={documents}
          pending={pending}
          error={error}
          onClose={() => setRejectOpen(false)}
          onSubmit={(payload) => {
            onDecide({ decision: 'reject', ...payload });
            setRejectOpen(false);
          }}
        />
      )}
    </div>
  );
}
